import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useViewerRoles } from '@treams/treams-hooks';
import { HeartRateIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { AnalyticsEngagementEnps } from '@treams/treams-network';
import { Empty, Skeleton, Table } from '@treams/treams-ui';

const Bar = styled(Box)<{ $color: string }>`
	height: 16px;
	background: ${(props) => props.theme.colors[props.$color]};
	color: white;
	text-align: center;
	line-height: 16px;
`;

function Distribution(props: { values: [number, number, number] }) {
	const sum = _.sum(props.values);

	return (
		<Flex style={{ borderRadius: 3, overflow: 'hidden', width: '100%' }}>
			<Bar width={((props.values[0] / sum) * 100).toFixed(2) + '%'} $color="red">
				{((props.values[0] / sum) * 100).toFixed(2) + '%'}
			</Bar>
			<Bar width={((props.values[1] / sum) * 100).toFixed(2) + '%'} $color="yellow">
				{((props.values[1] / sum) * 100).toFixed(2) + '%'}
			</Bar>
			<Bar width={((props.values[2] / sum) * 100).toFixed(2) + '%'} $color="green">
				{((props.values[2] / sum) * 100).toFixed(2) + '%'}
			</Bar>
		</Flex>
	);
}

function PercentageDisplayValue(props: { total: number; completed: number }) {
	const percentage = Math.round((props.completed / Math.max(props.total, 1)) * 100);

	return `${props.completed} / ${props.total} (${percentage}%)`;
}

type EngagementGroupsENPSData = {
	detractors?: number | null;
	passives?: number | null;
	promoters?: number | null;
	score?: number | null;
	group: { id: string; name: string };
	totalRequestsSent?: number | null;
	totalRequestsCompleted?: number | null;
};

function NpsAnalyticsView(props: {
	filters: graphql.AnalyticsFilters;
	data?: {
		engagementENPS: AnalyticsEngagementEnps;
		engagementGroupsENPS: EngagementGroupsENPSData[];
	};
}) {
	const { t } = useTranslation();

	return (
		<Table>
			<Table.Header>
				<Table.Cell basis={0.2}>{t('analytics.engagement.nps.header.department')}</Table.Cell>
				{/* <Table.Cell basis={0.1}>{t('analytics.engagement.nps.header.employees')}</Table.Cell> */}
				<Table.Cell basis={0.17}>{t('analytics.engagement.nps.header.responses')}</Table.Cell>
				<Table.Cell basis={0.5}>{t('analytics.engagement.nps.header.distribution')}</Table.Cell>
				<Table.Cell basis={0.15}>{t('analytics.engagement.nps.header.score')}</Table.Cell>
			</Table.Header>
			{props.data ? (
				props.data.engagementGroupsENPS.length === 0 &&
				typeof props.data.engagementENPS.score !== 'number' ? (
					<Skeleton.Empty
						contents={
							<Empty
								small
								icon={<HeartRateIcon />}
								title={t('analytics.engagement.nps.empty.title')}
								description={t('analytics.engagement.nps.empty.description')}
							/>
						}
					>
						<Skeleton.Repeat count={5}>
							<Table.Row>
								<Table.Cell.Skeleton basis={0.2} />
								{/* <Table.Cell.Skeleton basis={0.1} /> */}
								<Table.Cell.Skeleton basis={0.17} />
								<Table.Cell.Skeleton basis={0.5} />
								<Table.Cell.Skeleton basis={0.15} />
							</Table.Row>
						</Skeleton.Repeat>
					</Skeleton.Empty>
				) : (
					<>
						{props.data.engagementGroupsENPS.map((record) => (
							<Table.Row key={record.group.id}>
								<Table.Cell basis={0.2}>{record.group.name}</Table.Cell>
								{/* <Table.Cell basis={0.1}>
									{_.sum([record.detractors ?? 0, record.passives ?? 0, record.promoters ?? 0])}
								</Table.Cell> */}
								<Table.Cell basis={0.17}>
									<PercentageDisplayValue
										total={record.totalRequestsSent ?? 0}
										completed={record.totalRequestsCompleted ?? 0}
									/>
								</Table.Cell>
								<Table.Cell basis={0.5}>
									<Distribution
										values={[record.detractors ?? 0, record.passives ?? 0, record.promoters ?? 0]}
									/>
								</Table.Cell>
								<Table.Cell basis={0.15}>{record.score}</Table.Cell>
							</Table.Row>
						))}
						<Table.Footer>
							<Table.Cell basis={0.2}>{t('analytics.engagement.nps.workspace.total')}</Table.Cell>
							{/* <Table.Cell basis={0.1}>
								{_.sum([
									props.data.engagementENPS.detractors ?? 0,
									props.data.engagementENPS.passives ?? 0,
									props.data.engagementENPS.promoters ?? 0,
								])}
							</Table.Cell> */}
							<Table.Cell basis={0.17}>
								<PercentageDisplayValue
									total={props.data.engagementENPS.totalRequestsSent ?? 0}
									completed={props.data.engagementENPS.totalRequestsCompleted ?? 0}
								/>
							</Table.Cell>
							<Table.Cell basis={0.5}>
								<Distribution
									values={[
										props.data.engagementENPS.detractors ?? 0,
										props.data.engagementENPS.passives ?? 0,
										props.data.engagementENPS.promoters ?? 0,
									]}
								/>
							</Table.Cell>
							<Table.Cell basis={0.15}>{props.data.engagementENPS.score}</Table.Cell>
						</Table.Footer>
					</>
				)
			) : (
				<Skeleton.Repeat count={5}>
					<Table.Row>
						<Table.Cell.Skeleton basis={0.2} />
						{/* <Table.Cell.Skeleton basis={0.1} /> */}
						<Table.Cell.Skeleton basis={0.17} />
						<Table.Cell.Skeleton basis={0.5} />
						<Table.Cell.Skeleton basis={0.15} />
					</Table.Row>
				</Skeleton.Repeat>
			)}
		</Table>
	);
}

export function EngagementNpsAnalytics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useEngagementNpsAnalyticsQuery({
		variables: {
			filters: props.filters,
		},
	});

	return (
		<NpsAnalyticsView
			filters={props.filters}
			data={
				query.data && {
					engagementENPS: query.data.engagementAnalytics.engagementENPS,
					engagementGroupsENPS: query.data.engagementAnalytics.engagementGroupsENPS,
				}
			}
		/>
	);
}

export function WorkspaceNpsAnalytics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useWorkspaceNpsAnalyticsQuery({
		variables: {
			filters: props.filters,
		},
	});

	return (
		<NpsAnalyticsView
			filters={props.filters}
			data={
				query.data && {
					engagementENPS: query.data.workspaceAnalytics.engagementENPS,
					engagementGroupsENPS: query.data.workspaceAnalytics.engagementGroupsENPS,
				}
			}
		/>
	);
}

export default function NpsAnalytics(props: { filters: graphql.AnalyticsFilters }) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementNpsAnalytics filters={props.filters} />;
	} else {
		return <WorkspaceNpsAnalytics filters={props.filters} />;
	}
}
